import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { theme, Typo, Icon, ConfirmDelete } from '../../../../ui';
import { useNewViewCourseId } from '../../../../hooks/readFromUrls';
import { getDarkerThemeColor, getLighterThemeColor, getUserThemeColor, shadeColor } from '../../../../ui/theme/colors';
import { useCoursePages, useCourseSections } from '../../hooks';
import ContextMenu from '../../../editCourse/components/ContextMenu';
import InlineEdit from '../../../editCourse/components/InlineEdit';
import { useTranslation } from 'react-i18next';
import { useReorderPagesMutation, useMovePageToSectionMutation } from '../../../editCourse/editCourseApi';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_SUCCESS } from '../../../../utils/constants';

import styled from 'styled-components';
import { SectionAccordion, SectionIcon, PageIcon, PageItem, DragHandleControl, MenuButtonControl, DraggablePage } from './components';
import { useSectionActions } from './hooks';
import { useFeatures } from '../../../settings/hooks';

const InfoIconWrapper = styled.span`
  display: flex;
  padding-left: 5px;
  align-items: center;
`;

const TocMenuSection = ({
  section,
  activePageId,
  active,
  themeColor,
  activePageRef,
  isStructureEditMode = false,
  sectionIndex = 0,
  totalSections = 0,
  isSectionDragging = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const courseId = useNewViewCourseId();

  const isEditContext = location.pathname.includes('/edit/');
  const effectiveIsLocked = isEditContext ? false : section.isLocked;

  const [expanded, setExpanded] = useState(!effectiveIsLocked);

  const darkColor = getDarkerThemeColor(themeColor);
  const userColor = getUserThemeColor(themeColor);
  const lightColor = getLighterThemeColor(themeColor);
  const ultralightColor = shadeColor(themeColor, 0.95);
  const coursePages = useCoursePages();
  const courseSections = useCourseSections();

  const handleNavigateToPage = useCallback(
    (pageId) => {
      const basePath = isEditContext ? '/edit/course' : '/view/course';
      navigate(`${basePath}/${courseId}/${pageId}`);
    },
    [navigate, courseId, isEditContext],
  );
  const features = useFeatures();

  const actions = useSectionActions({
    courseId,
    section,
    sectionIndex,
    totalSections,
    coursePages,
    courseSections,
    activePageId,
    isEditContext,
    onNavigateToPage: handleNavigateToPage,
    quizEnabled: !!features?.quiz,
  });

  useEffect(() => {
    if (active && !effectiveIsLocked) {
      setExpanded(true);
    }
  }, [active, effectiveIsLocked]);

  const handleChange = useCallback(() => {
    if (!effectiveIsLocked) {
      setExpanded((prev) => !prev);
    }
  }, [effectiveIsLocked]);

  const navigateToPage = useCallback(
    (pageId, isLocked) => {
      const canNavigate = isEditContext || (!isLocked && !effectiveIsLocked);
      if (canNavigate) {
        const url = isEditContext ? `/edit/course/${courseId}/${pageId}` : `/view/course/${courseId}/${pageId}`;
        navigate(url);
      }
    },
    [navigate, courseId, effectiveIsLocked, isEditContext],
  );

  const handleSectionContextMenu = useCallback(
    (e) => {
      if (!isStructureEditMode) return;
      actions.openSectionContextMenu(e);
    },
    [isStructureEditMode, actions],
  );

  const handlePageContextMenu = useCallback(
    (e, page) => {
      if (!isStructureEditMode) return;
      actions.openPageContextMenu(e, page);
    },
    [isStructureEditMode, actions],
  );

  const isQuiz = section.type === 'quiz';
  const sectionPages = coursePages.filter((page) => page.sectionId === section.id);

  const [reorderPages] = useReorderPagesMutation();
  const [movePageToSection] = useMovePageToSectionMutation();

  const handlePageDrop = useCallback(
    async (draggedPageId, targetSectionId, targetIndex) => {
      try {
        const draggedPageObj = coursePages.find((p) => p.id === draggedPageId);
        if (!draggedPageObj) return;

        const sourceSectionId = draggedPageObj.sectionId;

        if (sourceSectionId === targetSectionId) {
          const pages = coursePages.filter((p) => p.sectionId === targetSectionId).sort((a, b) => a.order - b.order);
          const currentIndex = pages.findIndex((p) => p.id === draggedPageId);
          if (currentIndex === targetIndex) return;

          const reorderedPages = [...pages];
          const [removed] = reorderedPages.splice(currentIndex, 1);
          const adjustedTargetIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
          reorderedPages.splice(adjustedTargetIndex, 0, removed);

          const pageIds = reorderedPages.map((p) => p.id);
          await reorderPages({ sectionId: targetSectionId, pageIds, courseId }).unwrap();
          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        } else {
          await movePageToSection({
            pageId: draggedPageId,
            sectionId: targetSectionId,
            position: targetIndex + 1,
            courseId,
          }).unwrap();
          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        }
      } catch (error) {
        console.error('Failed to move page:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      }
    },
    [coursePages, reorderPages, movePageToSection, courseId, t],
  );

  const renderHeaderContent = () => (
    <>
      {isStructureEditMode && <DragHandleControl />}

      {!isStructureEditMode && <SectionIcon section={section} isLocked={effectiveIsLocked} darkColor={darkColor} userColor={userColor} />}

      <InlineEdit
        isEditing={isEditContext && actions.structureActions.inlineEditItem?.type === 'section' && actions.structureActions.inlineEditItem?.id === section.id}
        value={section.name}
        onSave={(newName) => actions.structureActions.handleRenameSection(section.id, newName)}
        onCancel={actions.structureActions.cancelInlineEdit}
        themeColor={themeColor}
        isLoading={actions.structureActions.isLoading}
        onStartEdit={isStructureEditMode ? () => actions.structureActions.startInlineEditSection(section) : undefined}
      >
        <Typo.Text color={effectiveIsLocked ? theme.colors.NEU_600 : theme.colors.NEU_PRIMARY}>{section.name}</Typo.Text>
      </InlineEdit>

      {isStructureEditMode && <MenuButtonControl onClick={actions.openSectionContextMenu} />}
    </>
  );

  const renderPageItem = (page, pageIndex) => {
    const effectivePageIsLocked = isEditContext ? false : page.isLocked;
    const isActivePage = page.id === activePageId;

    const pageContent = (
      <PageItem
        ref={isActivePage ? activePageRef : null}
        isActive={isActivePage}
        isLocked={effectiveIsLocked || effectivePageIsLocked}
        isMoving={actions.movingItem?.type === 'page' && actions.movingItem?.id === page.id}
        sectionIsActive={active}
        lightColor={isActivePage ? lightColor : ultralightColor}
        darkColor={darkColor}
        onClick={() => navigateToPage(page.id, effectivePageIsLocked)}
        onContextMenu={(e) => handlePageContextMenu(e, page)}
      >
        {isStructureEditMode && <DragHandleControl disabled={isQuiz} />}

        {!isStructureEditMode && (
          <PageIcon page={page} isLocked={effectivePageIsLocked} isSectionLocked={effectiveIsLocked} isEditMode={isEditContext} darkColor={darkColor} />
        )}

        <InlineEdit
          isEditing={isEditContext && actions.structureActions.inlineEditItem?.type === 'page' && actions.structureActions.inlineEditItem?.id === page.id}
          value={page.name}
          onSave={(newName) => actions.structureActions.handleRenamePage(page.id, newName)}
          onCancel={actions.structureActions.cancelInlineEdit}
          themeColor={themeColor}
          isLoading={actions.structureActions.isLoading}
          onStartEdit={isStructureEditMode ? () => actions.structureActions.startInlineEditPage(page) : undefined}
          onClick={() => navigateToPage(page.id, effectivePageIsLocked)}
        >
          <Typo.MediumText color={effectiveIsLocked || effectivePageIsLocked ? theme.colors.NEU_600 : theme.colors.NEU_PRIMARY}>{page.name}</Typo.MediumText>
        </InlineEdit>

        {isStructureEditMode && <MenuButtonControl onClick={(e) => actions.openPageContextMenu(e, page)} />}
      </PageItem>
    );

    if (isStructureEditMode) {
      return (
        <DraggablePage key={page.id} page={page} sectionId={section.id} index={pageIndex} onDrop={handlePageDrop} themeColor={themeColor} isQuiz={isQuiz}>
          {pageContent}
        </DraggablePage>
      );
    }

    return <React.Fragment key={page.id}>{pageContent}</React.Fragment>;
  };

  return (
    <>
      <SectionAccordion
        expanded={expanded}
        onChange={handleChange}
        isActive={active}
        isLocked={effectiveIsLocked}
        isMoving={actions.movingItem?.type === 'section' && actions.movingItem?.id === section.id}
        lightColor={ultralightColor}
        onContextMenu={handleSectionContextMenu}
        headerContent={renderHeaderContent()}
        isCollapsed={isSectionDragging}
      >
        {sectionPages.map(renderPageItem)}
      </SectionAccordion>

      {actions.contextMenu && <ContextMenu position={actions.contextMenu} items={actions.getContextMenuItems()} onClose={actions.closeContextMenu} />}

      <ConfirmDelete
        open={actions.structureActions.deleteDialog.open}
        onClose={actions.structureActions.closeDeleteDialog}
        onAcceptDelete={actions.structureActions.handleDeleteConfirm}
        title={actions.structureActions.deleteDialog.type === 'section' ? t('editCourse.deleteSection') : t('editCourse.deletePage')}
        description={
          actions.structureActions.deleteDialog.type === 'section'
            ? t('editCourse.deleteSectionConfirm', { name: actions.structureActions.deleteDialog.item?.name })
            : t('editCourse.deletePageConfirm', { name: actions.structureActions.deleteDialog.item?.name })
        }
      />
    </>
  );
};

export default TocMenuSection;
